import Component from '@glimmer/component';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';
import { PhCheckCircle, PhXCircle } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import type { StepDefinition } from './wizard';

interface ProgressTrackerSignature {
  Args: {
    steps: StepDefinition[];
    currentStepIndex: number;
    furthestVisitedStepIndex?: number;
    onGoToStep?: (index: number) => void;
  };
  Element: HTMLDivElement;
}

export default class ProgressTracker extends Component<ProgressTrackerSignature> {
  handleGoToStep = (index: number) => {
    this.args.onGoToStep?.(index);
  };

  isPastStep = (index: number) => {
    return index < this.args.currentStepIndex;
  };

  isAccessibleStep = (index: number) => {
    const furthestVisitedStepIndex =
      this.args.furthestVisitedStepIndex ?? this.args.currentStepIndex;

    return index !== this.args.currentStepIndex && index <= furthestVisitedStepIndex;
  };

  stepStatus = (index: number) => {
    const furthestVisitedStepIndex =
      this.args.furthestVisitedStepIndex ?? this.args.currentStepIndex;
    const isVisitedStep =
      index < this.args.currentStepIndex ||
      (index > this.args.currentStepIndex && index <= furthestVisitedStepIndex);

    if (isVisitedStep) {
      if (this.args.steps[index]?.isComplete ?? false) {
        return 'completed';
      }
      return 'incomplete';
    }
    if (index === this.args.currentStepIndex) {
      return 'current';
    }
    return 'upcoming';
  };

  <template>
    <Section ...attributes>
      <:header>
        <span class="tio-text-sm-semibold tracking-wide uppercase tio-fg-muted">{{t
            "tas.guided.progress"
          }}</span>
      </:header>
      <:body>
        <nav aria-label={{t "tas.guided.application_progress"}} class="py-2">
          <ol class="relative">
            {{#each @steps as |step index|}}
              {{#let (this.stepStatus index) as |status|}}
                <li
                  class="relative tio-row-top pb-6 last:pb-0"
                  aria-current={{if (eq status "current") "step"}}
                >
                  {{! Connecting line }}
                  {{#if (this.isNotLastStep index)}}
                    <div
                      class="absolute left-3.25 top-7 w-0.5 h-[calc(100%-12px)]
                        {{if
                          (eq status 'completed')
                          'tio-bg-info'
                          (if (eq status 'incomplete') 'tio-bg-danger' 'bg-gray-200')
                        }}"
                    ></div>
                  {{/if}}

                  {{! Step indicator }}
                  {{#if (eq status "completed")}}
                    <span
                      class="relative shrink-0 z-10 tio-row-center w-7 h-7 rounded-full tio-bg-surface"
                    >
                      <PhCheckCircle @size="28" @weight="fill" class="tio-fg-info" />
                    </span>
                  {{else if (eq status "incomplete")}}
                    <span
                      class="relative shrink-0 z-10 tio-row-center w-7 h-7 rounded-full tio-bg-danger-soft ring-2 tio-ring-danger"
                    >
                      <PhXCircle @size="28" @weight="fill" class="tio-fg-danger" />
                    </span>
                  {{else if (eq status "current")}}
                    <span
                      class="tio-inline-row-center w-7 h-7 rounded-full tio-bg-brand tio-fg-inverse tio-text-xs-bold shrink-0 z-10"
                    >{{this.displayNumber index}}</span>
                  {{else}}
                    <span
                      class="tio-inline-row-center w-7 h-7 rounded-full border-2 tio-border-default tio-fg-disabled tio-text-xs-medium shrink-0 z-10 tio-bg-surface"
                    >{{this.displayNumber index}}</span>
                  {{/if}}

                  {{! Step label + status }}
                  {{#if (this.isAccessibleStep index)}}
                    <button
                      type="button"
                      class="tio-col-flush min-w-0 text-left cursor-pointer group"
                      {{on "click" (fn this.handleGoToStep index)}}
                    >
                      <span
                        class="text-sm leading-tight group-hover:tio-fg-brand transition-colors
                          {{if
                            (eq status 'completed')
                            'font-medium tio-fg-secondary'
                            (if
                              (eq status 'incomplete')
                              'font-medium tio-fg-danger-body'
                              'font-medium tio-fg-secondary'
                            )
                          }}"
                      >{{step.label}}</span>
                      <span
                        class="text-xs mt-0.5
                          {{if
                            (eq status 'completed')
                            'tio-fg-info'
                            (if (eq status 'incomplete') 'tio-fg-danger' 'tio-fg-disabled')
                          }}"
                      >
                        {{#if (eq status "completed")}}
                          {{t "tas.guided.steps.complete"}}
                        {{else if (eq status "incomplete")}}
                          {{t "tas.guided.steps.incomplete"}}
                        {{/if}}
                      </span>
                    </button>
                  {{else}}
                    <div class="tio-col-flush min-w-0">
                      <span
                        class="text-sm leading-tight
                          {{if
                            (eq status 'current')
                            'font-semibold tio-fg-primary'
                            'tio-fg-disabled'
                          }}"
                      >{{step.label}}</span>

                    </div>
                  {{/if}}
                </li>
              {{/let}}
            {{/each}}
          </ol>
        </nav>
      </:body>
    </Section>
  </template>

  displayNumber = (index: number) => {
    return index + 1;
  };

  isNotLastStep = (index: number) => {
    return index !== this.args.steps.length - 1;
  };
}
